/*
 * Copyright (c) 2025 Bolmate B.V.
 */

import {getSetting} from '@dvrd/dvr-controls';

import React, {MouseEventHandler, useContext, useRef, useState} from 'react';

import {useNavigate} from 'react-router';

import Subscription from '../../../../models/subscriptionModel';

import {paymentInfoEvent} from '../../../../util/analytics';

import {ActivateHandle, CancelType} from '../../../../util/interfaces';

import {startCheckout} from '../../../../util/subscriptionUtil';

import {AppContext} from '../../../misc/appContext';

import CancelSubscriptionController from '../cancelSubscription/cancelSubscriptionController';

import SubscriptionCouponController from '../coupon/subscriptionCouponController';

import CustomerDetailsController from '../customerDetails/customerDetailsController';

import PaymentMethod from '../paymentMethod';

import SetVatNumberController from '../setVatNumber/setVatNumberController';

import StatsActions from './statsActions';

interface Props {
    onClickFailedPayment: MouseEventHandler;
    onClickStart: MouseEventHandler;
    onClickPortal: MouseEventHandler;
    loadSubscription: VoidFunction;
    orderCount: number;
    subscription: Subscription;
    loading: boolean;
    usersEstimate: number;
}

export default function StatsActionsController(props: Props) {
    const {
        loadSubscription,
        orderCount,
        subscription,
        usersEstimate,
        onClickFailedPayment,
        onClickStart,
        onClickPortal,
        loading
    } = props;
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {user} = context.userContext;
    const [loadingPayment, setLoadingPayment] = useState(false);
    const [paymentActive, setPaymentActive] = useState(false);
    const customerDetailsRef = useRef<ActivateHandle>(null);
    const couponRef = useRef<ActivateHandle>(null);
    const vatNumberRef = useRef<ActivateHandle>(null);
    const cancelRef = useRef<ActivateHandle>(null);
    const navigate = useNavigate();

    function onOpenCustomerDetails() {
        customerDetailsRef.current?.activate();
    }

    function onClickCoupon() {
        couponRef.current?.activate();
    }

    function onActivatePayment(_subscription?: Subscription | null) {
        return function () {
            if (!_subscription) _subscription = user?.subscription;
            if (!_subscription) return;
            if (!_subscription.vatNumber) return vatNumberRef.current?.activate();
            setPaymentActive(true);
            paymentInfoEvent();
        };
    }

    function onVatNumberSuccess(subscription: Subscription) {
        if (user) {
            user.subscription = subscription;
            context.onChangeContext({user});
        }
        onActivatePayment(subscription)();
    }

    function onCancelSubscription() {
        cancelRef.current?.activate();
    }

    function onCancelSuccess() {
        window.location.reload();
    }

    function onChoosePaymentMethod(paymentMethod: 'ideal' | 'cc' | 'sepa') {
        return function () {
            if (paymentMethod === 'ideal') navigate('/ideal');
            else onStartCheckout(paymentMethod);
        };
    }

    function onStartCheckout(paymentMethod: 'cc' | 'sepa') {
        if (!account) return;
        setLoadingPayment(true);
        const appUrl = getSetting('appUrl'),
            returnUrl = appUrl + '/facturen?subscription=1',
            cancelUrl = appUrl + '/instellingen/abonnement',
            method = paymentMethod === 'cc' ? 'card' : 'sepa_debit';
        startCheckout(account.id, returnUrl, cancelUrl, undefined, method);
    }

    function onClosePayment() {
        setPaymentActive(false);
        setLoadingPayment(false);
    }

    return (
        <>
            <StatsActions
                onOpenCustomerDetails={onOpenCustomerDetails}
                onClickPortal={onClickPortal}
                onClickCoupon={onClickCoupon}
                onClickFailedPayment={onClickFailedPayment}
                onClickStart={onClickStart}
                onCancelSubscription={onCancelSubscription}
                orderCount={orderCount}
                loading={loading}
                subscription={subscription}
            />
            <CustomerDetailsController ref={customerDetailsRef} />
            <SubscriptionCouponController ref={couponRef} onSuccess={loadSubscription} />
            <SetVatNumberController ref={vatNumberRef} onSuccess={onVatNumberSuccess} />
            <CancelSubscriptionController ref={cancelRef} type={CancelType.SUBSCRIPTION} onSuccess={onCancelSuccess} />
            <PaymentMethod
                onChoose={onChoosePaymentMethod}
                onClose={onClosePayment}
                active={paymentActive}
                loading={loadingPayment}
                activeUsersEstimate={usersEstimate}
            />
        </>
    );
}

StatsActionsController.displayName = 'StatsActionsController';
